import { ActivityType, ActivityVariable } from "../../../types/activity";
import {
  PerformanceActivity,
  PerformanceLog,
  PerformanceType,
  Session,
  TrainingLog,
} from "../../../types/post";
import { PeakEvent, eventIncludesDay } from "../../../types/event";
import {
  PerformanceDraft,
  PerformanceResultDraft,
} from "../../../types/performance";
import { VariableTarget } from "./ActivityResultEditor";

/** UI state of one result row (step 3). */
export interface ResultRowState {
  key: string; // stable React key
  id?: string; // existing performanceLogs id (edit mode)
  activity: ActivityType | null;
  variables: ActivityVariable[];
  selectedIndices: Set<number>;
  performanceExpectation: number;
  notes: string;
  isPB: boolean;
  isSB: boolean;
  /** Lower-cased variable name → target (session ActivityVariable.goal). */
  targets?: Record<string, VariableTarget>;
}

/** Event half of the step-2 choice (Events.md §6). */
export type EventChoice =
  | { kind: "none" }
  | { kind: "event"; eventId: string }
  | { kind: "create"; name: string };

export const STEP_TITLES = ["Intent", "Link", "Results", "Load", "Complete"];

let rowCounter = 0;
export const nextRowKey = () => `row_${Date.now()}_${rowCounter++}`;

/** Fresh empty result row. */
export function emptyRow(): ResultRowState {
  return {
    key: nextRowKey(),
    activity: null,
    variables: [],
    selectedIndices: new Set(),
    performanceExpectation: 5,
    notes: "",
    isPB: false,
    isSB: false,
  };
}

/** Build an ActivityType stub from a denormalised activity (goal/result/session). */
function activityStub(
  activityTypeID: string,
  activityName: string,
  variableNames: string[],
  categoryId?: string,
): ActivityType {
  return {
    id: activityTypeID,
    name: activityName,
    description: "",
    variables: variableNames,
    ...(categoryId ? { categoryId, isFromPack: true } : {}),
  };
}

/** Rows prefilled from a session's activities that carry a target `goal` (ActivityVariable.goal). */
export function rowsFromSession(
  session: Session,
  resolveName: (activityTypeID: string, fallback?: string) => string,
): ResultRowState[] {
  const rows: ResultRowState[] = [];
  (session.ActivityMeasurementList ?? []).forEach((m) => {
    const withGoal = m.variables.filter(
      (v) => typeof v.goal === "number" && v.goal !== null,
    );
    if (withGoal.length === 0) return;
    const targets: Record<string, VariableTarget> = {};
    withGoal.forEach((v) => {
      targets[v.name.toLowerCase()] = { goal: v.goal as number, unit: v.unit };
    });
    const variables: ActivityVariable[] = m.variables.map((v) => ({
      name: v.name,
      measurement: null,
      unit: v.unit ?? "",
    }));
    rows.push({
      ...emptyRow(),
      activity: activityStub(
        m.ActivityTypeID,
        resolveName(m.ActivityTypeID, m.activityName),
        m.variables.map((v) => v.name),
        m.globalCategoryID,
      ),
      variables,
      selectedIndices: new Set(variables.map((_, i) => i)),
      targets,
    });
  });
  return rows;
}

/** Row seeded from an existing result document (edit mode). */
export function rowFromResult(
  result: PerformanceLog & { id: string },
): ResultRowState {
  const activity = result.activity;
  const variables = activity?.variables ?? [];
  return {
    key: nextRowKey(),
    id: result.id,
    activity: activity
      ? activityStub(
          activity.activityTypeID,
          activity.activityName,
          variables.map((v) => v.name),
          activity.globalCategoryID,
        )
      : null,
    variables: variables.map((v) => ({ ...v })),
    selectedIndices: new Set(variables.map((_, i) => i)),
    performanceExpectation: result.performanceExpectation ?? 5,
    notes: result.notes ?? "",
    isPB: !!result.tag?.includes("PB"),
    isSB: !!result.tag?.includes("SB"),
  };
}

/**
 * Seed rows from the target values of the sessions linked to an event,
 * skipping activities already present in `existing`.
 */
export function rowsFromEventSessions(
  sessions: Session[],
  resolveName: (activityTypeID: string, fallback?: string) => string,
  existing: ResultRowState[] = [],
): ResultRowState[] {
  const seen = new Set(
    existing.map((r) => r.activity?.id).filter((id): id is string => !!id),
  );
  const out: ResultRowState[] = [];
  sessions.forEach((session) => {
    rowsFromSession(session, resolveName).forEach((row) => {
      const id = row.activity?.id;
      if (id && seen.has(id)) return;
      if (id) seen.add(id);
      out.push(row);
    });
  });
  return out;
}

/** The PerformanceActivity payload for a row (only selected variables). */
export function rowToActivity(row: ResultRowState): PerformanceActivity | null {
  if (!row.activity?.id) return null;
  const variables = row.variables.filter((_, i) => row.selectedIndices.has(i));
  return {
    activityTypeID: row.activity.id,
    activityName: row.activity.name,
    variables,
    ...(row.activity.categoryId
      ? { globalCategoryID: row.activity.categoryId }
      : {}),
  };
}

/** A row is complete when it has an activity and ≥1 measured variable. */
export function rowIsValid(row: ResultRowState): boolean {
  if (!row.activity?.id) return false;
  return row.variables.some(
    (v, i) =>
      row.selectedIndices.has(i) &&
      v.measurement !== null &&
      v.measurement !== undefined &&
      !Number.isNaN(v.measurement),
  );
}

/** A row the athlete started but did not fill (ignored on save). */
export function rowIsEmpty(row: ResultRowState): boolean {
  return !row.activity?.id;
}

export function rowToResultDraft(row: ResultRowState): PerformanceResultDraft | null {
  const activity = rowToActivity(row);
  if (!activity) return null;
  const tags: string[] = [];
  if (row.isPB) tags.push("PB");
  if (row.isSB) tags.push("SB");
  const draft: PerformanceResultDraft = {
    activity,
    performanceExpectation: row.performanceExpectation,
    notes: row.notes,
    tag: tags,
  };
  if (row.id) draft.id = row.id;
  return draft;
}

/** Non-withdrawn events whose `date..lastDay` includes `dateID` (Events.md §6). */
export function eventsSpanningDate(
  events: PeakEvent[],
  dateID: string,
): PeakEvent[] {
  return events.filter(
    (e) => !!e.date && e.status !== "withdrawn" && eventIncludesDay(e, dateID),
  );
}

/** Seed shared by every entry point (PerformanceLog.md §3.1). */
export interface PerformanceFlowSeed {
  performanceType?: PerformanceType;
  /** Event the flow was opened from (Schedule sheet, Dashboard "Events to log"). */
  eventId?: string;
  /** 0-based step to open on. */
  startStep?: number;
}

export type { PerformanceDraft, TrainingLog };
